import { Container, Sprite, Texture } from 'pixi.js';
import { AssetKeys } from './Assets';
import { BODY, type BenjiSim } from '@benji/sim-core';

/**
 * Render-only view of an authoritative {@link BenjiSim}.
 *
 * Owns no physics and no clock: every frame it mirrors the sim's transform
 * onto the sprites and runs the purely-visual hold/jump pose cross-fade
 * (cosmetic — not part of the deterministic outcome).
 */
export class BenjiView extends Container {
  readonly sprite: Container;
  private readonly holdSprite: Sprite;
  private readonly jumpSprite: Sprite;

  private holdPoseAlpha = 1;
  private holdPoseTarget = 1;
  private poseBlendFrom = 1;
  private poseBlendElapsed = BODY.poseBlend;

  constructor(textures: Record<string, Texture>) {
    super();
    this.sprite = new Container();
    this.holdSprite = new Sprite(textures[AssetKeys.BenjiHold]);
    this.jumpSprite = new Sprite(textures[AssetKeys.BenjiJump]);
    this.holdSprite.anchor.set(0.85, 0.5);
    this.jumpSprite.anchor.set(0.85, 0.5);
    this.jumpSprite.alpha = 0;
    this.sprite.addChild(this.holdSprite, this.jumpSprite);
    this.addChild(this.sprite);
  }

  setDisplayScale(scale: number) {
    this.sprite.scale.set(scale);
  }

  /**
   * Mirror the sim onto the display, interpolated by `alpha` (the leftover
   * fixed-step accumulator fraction, 0..1) between the pose at the start of
   * the last tick and the current one — smooth at any refresh rate. `dt`
   * drives only the cosmetic pose fade.
   */
  sync(sim: BenjiSim, alpha: number, dt: number) {
    this.position.set(
      sim.rPrevX + (sim.worldX - sim.rPrevX) * alpha,
      sim.rPrevY + (sim.worldY - sim.rPrevY) * alpha,
    );
    // Shortest-arc interpolation for the body rotation.
    let dr = sim.bodyRotation - sim.rPrevRot;
    while (dr > Math.PI) dr -= Math.PI * 2;
    while (dr < -Math.PI) dr += Math.PI * 2;
    this.sprite.rotation = sim.rPrevRot + dr * alpha;
    this.updatePose(sim, dt);
  }

  private updatePose(sim: BenjiSim, dt: number) {
    const mode = sim.getMode();
    const nextTarget = mode === 'fly' || mode === 'fall' ? 0 : 1;
    if (this.holdPoseTarget !== nextTarget) {
      this.poseBlendFrom = this.holdPoseAlpha;
      this.holdPoseTarget = nextTarget;
      this.poseBlendElapsed = 0;
    }
    if (this.holdPoseAlpha === this.holdPoseTarget && this.poseBlendElapsed >= BODY.poseBlend) {
      return;
    }
    this.poseBlendElapsed = Math.min(this.poseBlendElapsed + dt, BODY.poseBlend);
    const t = BODY.poseBlend <= 0 ? 1 : this.poseBlendElapsed / BODY.poseBlend;
    const eased = t * t * (3 - 2 * t);
    this.holdPoseAlpha = this.poseBlendFrom + (this.holdPoseTarget - this.poseBlendFrom) * eased;
    this.holdSprite.alpha = this.holdPoseAlpha;
    this.jumpSprite.alpha = 1 - this.holdPoseAlpha;
  }
}
